/* eslint-disable no-param-reassign */
import DocsConfig from '@tencent/docs-config';
import { ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { ActionType } from '../../../editor/src/js/constants/common-action-type';
import { generateProduceNew, generateProduceTemplate, openTemplateList } from '@frame/components/features/produce-new/produce-new-manager';
import { setNewWindowUrl } from '@tencent/tencent_doc_open_url';
import i18n from '@i18next/i18n';
import { isWeworkUser } from '@src/js/common/utils';
import { SYNC_CONFIG_MAP } from '@src/slide/application/service/sync-file/core/constants';
import { INSERT_SLIDE_ORIGIN_TYPE } from '@src/js/constant';
import { useSyncToggle } from '@src/slide/application/service/sync-file/core/utils';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { openUrlForAll } from '@src/slide/infrastructure/module/independent/url/open-url';

/**
 * 注意：
 * 此文件方法为原菜单三道杠button的菜单项对应方法，与三道杠所有的方法配置都在这里做
 * 如果有新加的方法建议加到menuManager里，这里直接进行调用
 * actionCallBack：
 * 负责处理组件抛回来的id事件。有部分方法由于菜单组件内置所以不做处理。菜单组件具体提供的方法id可以import相关参数进行查看。
 * renderCallBack：
 * 负责在渲染时告诉组件菜单项和子菜单项是否需要加一些变化条件，比如显隐、禁用和选择状态等。
 */

interface ShortcutInfo {
    url: string;
    fname: string;
    sheet: boolean;
}

/**
 * 从原来button处搬运来的创建快捷方式的方法。
 * @returns 创建链接
 */
export function creatShortcut(): string {
    let url = window.location.href;
    url = url.replace('previewtip=1', '');
    const info: ShortcutInfo = {
        url,
        fname: SlideDataServices.SlideTitleManager()?.title ?? '',
        sheet: false,
    };
    const encodedInfo = encodeURIComponent(JSON.stringify(info));
    return `https://${window.hostname}/preview/link.url?info=${encodedInfo}`;
}

/**
 * 从原来button处搬运来的打开侵权指引入口的方法。
 */
function infringement() {
    // 侵权指引入口
    const url = `https://${window.hostname}/doc/DYWhSRFFtQWdsY2hx?pub=1&dver=2.1.0`;
    const options = {
        url,
        replace: false,
        windowName: '_blank',
    };
    openUrlForAll(options);
}

/**
 * 打开新的页面通用方法
 * @param path 页面链接
 */
function commonOpenPage(path: string): void {
    openUrlForAll({
        url: `${window.location.origin}${path}`,
        replace: false,
        windowName: '_blank',
    });
}

/**
 * 把需要判断开关和参数特殊的部分方法封装一下。
 * @param type 打开新建文档页的品类id
 */
function createNewDoc(type: string): void {
    const docType = {
        [ActionType.ONLINE_DOC]: 'doc',
        [ActionType.ONLINE_SHEET]: 'sheet',
        [ActionType.ONLINE_DEMO]: 'slide',
        [ActionType.ONLINE_COLLECT]: 'form',
    };
    switch (type) {
        case ActionType.ONLINE_DOC:
        case ActionType.ONLINE_SHEET:
        case ActionType.ONLINE_DEMO:
            openTemplateList(docType[type]);
            break;
        case ActionType.ONLINE_COLLECT:
            openTemplateList(docType[type]);
            break;
        default:
            createMore(type);
    }
}

/**
 * 把创建更多和通过模板创建的封装一下。
 * @param type 打开新建文档页的品类id
 */
function createMore(id: string): void {
    switch (id) {
        case ActionType.ONLINE_MIND:
        case ActionType.ONLINE_FLOWCHART:
            generateProduceNew(id);
            break;
        case ActionType.ONLINE_TEMPLATE:
            generateProduceTemplate();
            break;
    }
}
function getAppServices() {
    return AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
}
/**
 * 把生成副本、模板、导出相关和权限合为一个
 * @param {*} id 打开新建文档页的品类id
 */
function saveAndAuth(id: string): void {
    switch (id) {
        case ActionType.GENERATE_SHORTCUT:
            setNewWindowUrl(window, creatShortcut());
            break;
        case ActionType.GENERATE_COPIES:
        {
        // B 访问 C，点击生成副本，弹出引导提示，同步类型不为"不同步"时生成副本
            const { produceBackupManager } = getAppServices().viewableServiceLayer;
            produceBackupManager.promise().then((produceBackupManager) => {
                produceBackupManager.generateProduceBackup();
            });
            break;
        }
        case ActionType.SAVE_TEMP:
            import(/* webpackChunkName: "service" */ '@tencent/docs-scenario-components-save-template/dist/pc').then((module) => {
                const showPCSavaTemplate = module.default;
                showPCSavaTemplate({
                    title: SlideDataServices.SlideTitleManager()?.title ?? '', // 必填
                    padId: window.clientVars ? window.clientVars.padId : '', // 必填
                    domainId: window.clientVars ? window.clientVars.domainId : '', // 必填
                });
            });
            break;
        case ActionType.TRANSFER_OWNERSHIP:
            getTransferAuthManager()?.promise()
                .then((transferAuthManager) => {
                    transferAuthManager.showTransferAuth();
                });
            break;
    }
}

/**
 * 把关于和反馈合为一个
 * @param {*} id 打开新建文档页的品类id
 */
function aboutAndFeedback(id: string): void {
    switch (id) {
        case ActionType.REPORT:
            getAppServices().viewableServiceLayer.accusationService.promise()
                .then((accusationService) => {
                    accusationService.showSupportPage();
                });
            break;
        case ActionType.ACCUSATION:
            getAppServices().viewableServiceLayer.accusationService.promise()
                .then((accusationService) => {
                    accusationService.showJuBaoPage();
                });
            break;
        case ActionType.INFRINGEMENT:
            infringement();
            break;
    }
}

/**
 * 隐私相关
 * @param id
 */
function privacy(id: string): void {
    switch (id) {
    // 隐私政策
        case ActionType.PRIVACY_POLICY:
            commonOpenPage('/doc/p/30cb57a3280b16bd48fb49001022ada97479339a?dver=2.1.0');
            break;
            // 第三方共享信息清单
        case ActionType.THIRD_PARTY_DATA_SHARING:
            commonOpenPage('/doc/p/e2ace9137ef7ec29008026d824100cc65be95035?dver=2.1.27232530');
            break;
            // 第三方应用授权管理
        case ActionType.THIRD_AUTHORIZATION_MANAGEMENT:
            commonOpenPage('/open/auth');
            break;
            // 个人信息收集清单
        case ActionType.PERSONAL_INFO_COLLECTION:
            commonOpenPage('/doc/p/3b224d79afd8fbb47477ce2c9f867c7665b6cd05?dver=2.1.27277463');
            break;
    }
}

function updateSyncFileType(id: string): void {
    if (!Object.keys(SYNC_CONFIG_MAP).includes(id)) {
        return;
    }

    const syncType = SYNC_CONFIG_MAP[id];
    getSyncFileManager()?.updateSyncFileType(syncType);
}

/**
 * 此方法用来执行组件抛出的id对应的方法
 * 目前button和menuManager有点耦合，其实方法应当收归menuManager管理
 * 重构时可以把这些方法也挪到menuManager里
 * @param {*} id 菜单项的id
 * @param {*} menuManager 存储于context的全局菜单方法集合（不是所有菜单方法都是全局方法）。
 */
export function actionCallBack(id: string, menuManager: any, object: any = {}): void {
    createNewDoc(id);
    saveAndAuth(id);
    aboutAndFeedback(id);
    showAccessibility(id, menuManager);
    privacy(id);
    updateSyncFileType(id);
    const { type, callback } = menuManager.topMenu();
    object.TriggerFrom = INSERT_SLIDE_ORIGIN_TYPE.TOP;
    callback(type, id, object);
}

// ToB隐藏项目
const tobCheckMap = {
    create: ActionType.NEW,
    save_as_file: ActionType.EXPORT,
    save_as_template: ActionType.SAVE_TEMP,
    save_as_shortcut: ActionType.GENERATE_SHORTCUT,
    generate_copy: ActionType.GENERATE_COPIES,
    change_record: ActionType.REVISION_RECORD,
    read_record: ActionType.BROWSE_RECORD,
    // transfer_auth: ActionType.TRANSFER_OWNERSHIP,
    permission_menu: ActionType.PERMISSION_MENU,
    about: ActionType.ABOUT,
    helper: ActionType.HELP,
    support: ActionType.REPORT,
    user_report: ActionType.ACCUSATION,
    rename: ActionType.RENAME,
};

const SyncActions = [
    ActionType.SYNC_TO_LIST,
    ActionType.NOT_SYNC,
    ActionType.SYNC_CURRENT_FILE,
    ActionType.SYNC_ALL_FILE,
    ActionType.SYNC_DESC,
];

/**
 * 负责在渲染时告诉组件菜单项和子菜单项是否需要加一些变化条件，比如显隐、禁用和选择状态等。
 * 原则上来说也该加到menuManager里来做，但是目前就是button和menuManager里都有。所以先搬过来用了。
 * 重构时可以把这些判断也挪到menuManager里，给其他invisible和selected也统一管理。
 * @param {*} id 菜单项的id
 * @param {*} menuManager 存储于context的全局菜单方法集合，可以用到一些设定好了的显隐规则。
 * @returns {} 菜单项的附加显示条件
 */
// eslint-disable-next-line complexity
export function renderCallBack(id: string, menuManager: IMenuManager | undefined) {
    const configs: any = {};
    // 使用menuManager中的禁用规则设定当前菜单项的禁用设定。
    const disableData = menuManager?.disableItemIds();
    // 品类不处理离线自动disable逻辑，通过config配置由workbench统一处理
    if (disableData && disableData.findIndex(item => item.id === id) > -1) {
        configs.disable = true;
    }
    // 权限相关隐藏项目判断
    if (authCheck(id) !== true) {
        configs.visible = authCheck(id);
    }
    // 其他相关隐藏项目判断隐藏
    if (!configCheck(id)) {
        configs.visible = false;
    }
    if (SyncActions.includes(id)) {
        return getSyncMenuConfigs(id);
    }
    return configs;
}

function getSyncMenuConfigs(id: string) {
    const syncFileManager = getSyncFileManager();
    if (id === ActionType.SYNC_TO_LIST) {
        return {
            description: syncFileManager?.getItemDescription(),
            visible: useSyncToggle(),
        };
    }
    if (id === ActionType.SYNC_DESC) {
        return {
            disable: true,
        };
    }
    if (Object.keys(SYNC_CONFIG_MAP).includes(id)) {
        const currentType = SYNC_CONFIG_MAP[id];
        return {
            selected: syncFileManager?.currentSyncType === currentType,
        };
    }
}

/**
 * 配置相关隐藏
 * 部分功能已有显隐逻辑判断(修订记录为例，会根据用户权限进行判断)
 * controller的shouldHide优先级最高会覆盖原有visible判断
 * 前置到renderCallback中判断
 */
function configCheck(id: string): boolean {
    switch (id) {
        case ActionType.REVISION_RECORD:
            return (DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.revisionRecord) ?? true);
        default:
            return true;
    }
}

// 权限相关隐藏
function authCheck(id: string) {
    switch (id) {
        case ActionType.APPEAL:
            return appealShow();
        case ActionType.SAVE_TEMP:
            return !isWeworkUser();
    }
    return true;
}


// 文档处于被举报状态时，只有文档所有者显示文档申诉入口
function appealShow() {
    const { isOwner, dirty } = SlideDataServices.DocInfoManager() ?? {};
    return isOwner && dirty === 1;
}

function getSyncFileManager() {
    return getAppServices().editableServiceLayer?.syncFileManager;
}

function getTransferAuthManager() {
    return getAppServices()?.editableServiceLayer?.transferAuthManager;
}

// 无障碍弹框
export function showAccessibility(id: string, menuManager: any): void {
    if (id === ActionType.ACCESSIBILITY) {
        menuManager.accessibilityManager?.promise().then((a11yManager) => {
            a11yManager.showAccessibilityModal();
        });
    }
}
